import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  getInstitucionAdmin,
  crearInstitucion,
  actualizarInstitucion,
  subirLogoInstitucion,
} from '../../services/institucionesService';
import { useInstitucion } from '../../context/InstitucionContext';
import InstitucionBrandingForm, {
  VALORES_BRANDING_VACIOS,
  valoresDesdeInstitucion,
  validarTema,
} from '../../components/admin/InstitucionBrandingForm';

// Alta y edición de instituciones (SuperAdmin): datos, dominios propios, marca y logo.
const AdminInstitucionForm = () => {
  const { id } = useParams();
  const esEdicion = Boolean(id);
  const history = useHistory();
  const { institucion: activa, paleta: colors, recargar } = useInstitucion();

  const [institucion, setInstitucion] = useState(null);
  const [valores, setValores] = useState(VALORES_BRANDING_VACIOS);
  const [slug, setSlug] = useState('');
  const [orden, setOrden] = useState(0);
  // Aparecer en el portal de la plataforma lo decide cada organización: por defecto, no.
  const [visibleEnPortal, setVisibleEnPortal] = useState(false);
  const [dominios, setDominios] = useState('');
  const [loading, setLoading] = useState(esEdicion);
  const [guardando, setGuardando] = useState(false);
  const [subiendoLogo, setSubiendoLogo] = useState(false);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    if (!esEdicion) return;
    getInstitucionAdmin(id)
      .then((data) => {
        setInstitucion(data);
        setValores(valoresDesdeInstitucion(data));
        setOrden(data.orden || 0);
        setVisibleEnPortal(Boolean(data.visibleEnPortal));
        setDominios(data.dominios.map((d) => d.host).join('\n'));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, esEdicion]);

  const handleChange = (nombre, valor) => {
    setMensaje(null);
    setValores((prev) => ({ ...prev, [nombre]: valor }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMensaje(null);

    const errorTema = validarTema(valores.tema);
    if (errorTema) {
      setError(errorTema);
      return;
    }

    const limpiarColor = (v) => (v && v.trim() ? v.trim().toLowerCase() : null);
    const datos = {
      nombre: valores.nombre.trim(),
      nombreCorto: valores.nombreCorto.trim(),
      descripcion: valores.descripcion.trim() || null,
      tituloPagina: valores.tituloPagina.trim() || null,
      colorPrimario: limpiarColor(valores.colorPrimario),
      colorSecundario: limpiarColor(valores.colorSecundario),
      colorAcento: limpiarColor(valores.colorAcento),
      tema: valores.tema.trim() ? JSON.stringify(JSON.parse(valores.tema)) : null,
      orden: Number(orden) || 0,
      visibleEnPortal,
      dominios: dominios
        .split(/[\n,;]+/)
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean),
    };

    setGuardando(true);
    try {
      if (esEdicion) {
        await actualizarInstitucion(id, datos);
        if (activa && String(activa.id) === String(id)) recargar();
      } else {
        await crearInstitucion({ ...datos, slug: slug.trim() || null });
      }
      history.push('/admin/instituciones');
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleLogo = async (e) => {
    const archivo = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!archivo) return;
    setError(null);
    setMensaje(null);
    setSubiendoLogo(true);
    try {
      const { logoUrl } = await subirLogoInstitucion(id, archivo);
      setInstitucion((prev) => ({ ...prev, logoUrl }));
      setMensaje('Logo actualizado.');
      if (activa && String(activa.id) === String(id)) recargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubiendoLogo(false);
    }
  };

  const inputClass =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent';
  const ring = { '--tw-ring-color': colors.primary };

  if (loading) {
    return <div className="p-12 text-center text-gray-400">Cargando...</div>;
  }

  return (
    <div className="max-w-3xl">
      <nav className="mb-6 text-sm">
        <Link to="/admin/instituciones" className="font-medium hover:opacity-80" style={{ color: colors.ctaText }}>
          ← Volver a instituciones
        </Link>
      </nav>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {esEdicion ? `Editar ${institucion ? institucion.nombreCorto : 'institución'}` : 'Nueva institución'}
      </h1>

      {(error || mensaje) && (
        <div
          className={`text-sm rounded-lg px-4 py-3 mb-5 border ${
            error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
          }`}
        >
          {error || mensaje}
        </div>
      )}

      {esEdicion && institucion && (
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Logo</h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div
              className="w-40 h-24 rounded-lg flex items-center justify-center p-3"
              style={{ background: `linear-gradient(135deg, ${valores.colorPrimario || '#334155'}, ${valores.colorSecundario || '#0f172a'})` }}
            >
              {institucion.logoUrl ? (
                <img src={`${process.env.PUBLIC_URL}${institucion.logoUrl}`} alt="Logo actual" className="max-h-full max-w-full drop-shadow" />
              ) : (
                <span className="text-white/70 text-xs">Sin logo</span>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                accept=".svg,.png,image/svg+xml,image/png"
                onChange={handleLogo}
                disabled={subiendoLogo}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              />
              <p className="text-xs text-gray-400 mt-2">SVG o PNG, máximo 2 MB.{subiendoLogo && ' Subiendo…'}</p>
            </div>
          </div>
        </section>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Slug (identificador en la URL)</label>
            {esEdicion ? (
              <>
                <input type="text" value={institucion ? institucion.slug : ''} disabled className={`${inputClass} bg-gray-50 font-mono text-gray-500`} />
                <p className="text-xs text-gray-400 mt-1">No cambia: las URLs y las carpetas de archivos dependen de él.</p>
              </>
            ) : (
              <>
                <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} maxLength={50} placeholder="se genera del nombre" className={`${inputClass} font-mono`} style={ring} />
                <p className="text-xs text-gray-400 mt-1">Minúsculas y guiones. Será la ruta en la plataforma: /centro-publicaciones/<strong>{slug || 'slug'}</strong>.</p>
              </>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Orden en el portal</label>
            <input type="number" value={orden} onChange={(e) => setOrden(e.target.value)} className={inputClass} style={ring} />
          </div>
        </div>

        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={visibleEnPortal}
              onChange={(e) => setVisibleEnPortal(e.target.checked)}
              className="mt-0.5"
              style={{ accentColor: colors.primary }}
            />
            <span>
              <span className="block text-sm font-medium text-gray-700">Mostrar en el portal de la plataforma</span>
              <span className="block text-xs text-gray-400 mt-0.5">
                Aparece en el directorio y sus publicaciones recientes se muestran en la portada de PUSAH.
                Sus publicaciones siguen siendo accesibles por su dirección aunque esté desmarcado.
              </span>
            </span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Dominios propios</label>
          <textarea
            value={dominios}
            onChange={(e) => setDominios(e.target.value)}
            rows={3}
            spellCheck={false}
            className={`${inputClass} font-mono text-sm`}
            style={ring}
            placeholder={'publicaciones.empresa.com\nwww.empresa.com'}
          />
          <p className="text-xs text-gray-400 mt-1">
            Uno por línea, sin https:// ni ruta. Al entrar por uno de estos dominios se muestra directamente esta institución.
            Además hay que crear el binding del dominio en IIS con su certificado.
          </p>
        </div>

        <InstitucionBrandingForm valores={valores} onChange={handleChange} colorRing={colors.primary} />

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={guardando}
            className="px-5 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
          >
            {guardando ? 'Guardando...' : esEdicion ? 'Guardar cambios' : 'Crear institución'}
          </button>
          <Link to="/admin/instituciones" className="px-5 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100">
            Cancelar
          </Link>
        </div>
      </form>
    </div>
  );
};

export default AdminInstitucionForm;
